import { Excalidraw, exportToBlob } from "@excalidraw/excalidraw";
import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import "@excalidraw/excalidraw/index.css";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AgentBoard, AgentBoardElements } from "@/types/agent";

interface AgentBoardDialogProps {
  board: AgentBoard | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBoardChange: (board: AgentBoard) => void | Promise<void>;
  uploadPreviewImage: (file: File) => Promise<{ path: string }>;
}

export const AgentBoardDialog = ({
  board,
  open,
  onOpenChange,
  onBoardChange,
  uploadPreviewImage,
}: AgentBoardDialogProps) => {
  const [draftElements, setDraftElements] = useState<AgentBoardElements>([]);
  const [draftFiles, setDraftFiles] = useState<BinaryFiles>({});
  const [isSavingBoard, setIsSavingBoard] = useState(false);
  const excalidrawApiRef = useRef<ExcalidrawImperativeAPI | null>(null);

  const parseBoardState = (state: string): AgentBoardElements => {
    if (!state.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(state);
      return Array.isArray(parsed) ? (parsed as AgentBoardElements) : [];
    } catch (error) {
      console.error("Failed to parse board state", error);
      return [];
    }
  };

  useEffect(() => {
    if (open && board) {
      setDraftElements(parseBoardState(board.state));
      setDraftFiles({});
    }
  }, [board, open]);

  const handleChange = (
    elements: AgentBoardElements,
    appState: unknown,
    files: BinaryFiles
  ) => {
    void appState;
    void files;

    if (!board) {
      return;
    }

    setDraftElements(elements);
    setDraftFiles(files);
  };

  const buildPreviewImage = async (): Promise<string | null> => {
    if (!board) {
      return null;
    }

    const nonDeletedElements = draftElements.filter((element) => !element.isDeleted);
    if (nonDeletedElements.length === 0) {
      return null;
    }

    const appState: Partial<AppState> =
      excalidrawApiRef.current?.getAppState() ?? {};
    const files = excalidrawApiRef.current?.getFiles() ?? draftFiles;

    const blob = await exportToBlob({
      elements: nonDeletedElements,
      appState,
      files,
      mimeType: "image/png",
    });
    const fileName =
      `${board.name.trim().replace(/[^a-z0-9-_]+/gi, "-") || "board"}.png`;
    const previewFile = new File([blob], fileName, { type: "image/png" });
    const uploadedPreview = await uploadPreviewImage(previewFile);
    return uploadedPreview.path;
  };

  const handleOpenChange = async (nextOpen: boolean) => {
    if (isSavingBoard) {
      return;
    }

    if (!nextOpen && board) {
      setIsSavingBoard(true);
      try {
        let previewImagePath: string | null = null;

        try {
          previewImagePath = await buildPreviewImage();
        } catch (error) {
          toast.error("Failed to save board preview image");
          console.error(error);
        }

        await onBoardChange({
          ...board,
          state: JSON.stringify(draftElements),
          previewImagePath,
        });
      } finally {
        setIsSavingBoard(false);
      }
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[min(1280px,98vw)] p-0">
        {board && (
          <>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>{board.name}</DialogTitle>
              <DialogDescription>
                Board changes are saved with the agent.
              </DialogDescription>
            </DialogHeader>

            <div className="h-[70vh] overflow-hidden border-y">
              <Excalidraw
                key={board.id}
                excalidrawAPI={(api) => {
                  excalidrawApiRef.current = api;
                }}
                initialData={{
                  elements: draftElements,
                  files: draftFiles,
                }}
                onChange={handleChange}
              />
            </div>

            <DialogFooter className="px-6 pb-6">
              <Button
                variant="outline"
                onClick={() => void handleOpenChange(false)}
                disabled={isSavingBoard}
              >
                {isSavingBoard ? "Saving..." : "Close"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
